import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  TablePagination,
  tablePaginationClasses as classes,
} from "@mui/base/TablePagination";
import { styled } from "@mui/system";
import {
  FaChevronRight,
  FaChevronLeft,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { format } from "date-fns";

import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

export default function HistoricalDataTable({
  headers = [],
  rows = [],
  fields = [],
  filterableFields = [],
  requestSort = () => {},
}) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [filters, setFilters] = React.useState("");
  const [appliedFilters, setAppliedFilters] = React.useState({});
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [chevronDirection, setChevronDirection] = useState(true);

  const toggleFilters = () => {
    setFiltersExpanded(!filtersExpanded);
  };

  const handleFilterChange = (header, value) => {
    setFilters({
      ...filters,
      [header]: value === "-1" ? undefined : value,
    });
    setPage(0);
  };

  const applyFilters = () => {
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    setFilters("");
    setAppliedFilters({});
  };

  const filteredRows = rows.filter((row) =>
    Object.entries(appliedFilters).every(([header, value]) =>
      header === "Date"
        ? row[header?.toLowerCase()]?.includes(value)
        : value === undefined || row[header.toLowerCase()] === value
    )
  );

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function FilterRow({ headers, fields, rows, onFilterChange }) {
    const uniqueValues = headers.reduce((acc, header, index) => {
      acc[header] = [...new Set(rows.map((row) => row[fields[index]]))];
      return acc;
    }, {});

    return (
      <tr>
        <td colSpan={headers?.length} style={{ borderBottom: "none" }}>
          {filterableFields?.map((header) => (
            <>
              {header === "Date" ? (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Date"
                    disableFuture
                    value={filters[header] && dayjs(filters[header])}
                    onChange={(val) => {
                      val?.isValid() &&
                        onFilterChange(
                          header,
                          format(new Date(val), "yyyy-MM-dd")
                        );
                    }}
                  />
                </LocalizationProvider>
              ) : (
                <select
                  defaultValue={filters[header] || "-1"}
                  id={`${header}-filter`}
                  onChange={(e) => onFilterChange(header, e.target.value)}
                >
                  <option value="-1">{header}</option>
                  {uniqueValues[header].map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              )}
            </>
          ))}
        </td>
      </tr>
    );
  }

  return (
    <table className="historical-table">
      <thead>
        <tr>
          <th colSpan={headers?.length} onClick={toggleFilters}>
            {filtersExpanded ? <FaChevronDown /> : <FaChevronRight />}
            <span style={{ marginLeft: "8px" }}>Filters</span>
          </th>
        </tr>
        {filtersExpanded && (
          <>
            <FilterRow
              headers={headers}
              fields={fields}
              rows={rows}
              onFilterChange={handleFilterChange}
            />
            <tr>
              <td colSpan={headers?.length} style={{ paddingTop: 0 }}>
                <button onClick={applyFilters}>Apply</button>
                <button className="link" onClick={clearFilters}>
                  Clear filters
                </button>
              </td>
            </tr>
          </>
        )}
        <tr>
          {headers.map((header) => (
            <th key={header}>
              {(() => {
                const currentPath = window.location.pathname.toLowerCase();
                const isGrStrategies = currentPath.includes("gr-strategies");
                if (!isGrStrategies) {
                  return (
                    <button
                      className="sort-button"
                      onClick={() => {
                        requestSort(header.toLowerCase());
                        setChevronDirection(!chevronDirection);
                      }}
                    >
                      <b>{header}</b>
                      &nbsp;
                      {chevronDirection ? <FaChevronDown /> : <FaChevronUp />}
                    </button>
                  );
                }
                return header;
              })()}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {(rowsPerPage > 0
          ? filteredRows.slice(
              page * rowsPerPage,
              page * rowsPerPage + rowsPerPage
            )
          : filteredRows
        ).map((row, index) => (
          <tr key={index}>
            {headers.map((header, index) => (
              <td key={index}>
                {header === "Date" && row[fields[index]] ? (
                  format(
                    new Date(
                      row[fields[index]]?.split("T")[0].replace(/-/g, "/")
                    ),
                    "MM/dd/yyyy"
                  )
                ) : header === "Article" ? (
                  <a href={row[fields[index]]} target="_blank">
                    {row["headline"]}
                  </a>
                ) : (
                  row[fields[index]]
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <CustomTablePagination
            rowsPerPageOptions={[10, 15, 25, { label: "All", value: -1 }]}
            colSpan={headers?.length}
            count={filteredRows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            slotProps={{
              select: {
                "aria-label": "Rows per page",
              },
              actions: {
                slots: {
                  nextPageIcon: FaChevronRight,
                  backPageIcon: FaChevronLeft,
                },
              },
            }}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </tr>
      </tfoot>
    </table>
  );
}

const CustomTablePagination = styled(TablePagination)(
  ({ theme }) => `
    & .${classes.spacer} {
      display: none;
    }

    & .${classes.toolbar}  {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      padding: 4px 0;

      @media (min-width: 768px) {
        flex-direction: row;
        align-items: center;
      }
    }

    & .${classes.selectLabel} {
      margin: 0;
    }

    & .${classes.select}{
      padding: 2px 0 2px 4px;
      border: 1px solid #DAE2ED;
      border-radius: 6px;
      background-color: transparent;
      color: #1C2025;
      transition: all 100ms ease;

      &:hover {
        background-color: #F3F6F9;
        border-color: #C7D0DD;
      }
    }

    & .${classes.displayedRows} {
      margin: 0;

      @media (min-width: 768px) {
        margin-left: auto;
      }
    }

    & .${classes.actions} {
      display: flex;
      gap: 6px;
      border: transparent;
      text-align: center;
    }

    & .${classes.actions} > button {
      display: flex;
      align-items: center;
      padding: 0;
      border: transparent;
      border-radius: 50%;
      background-color: transparent;
      color: #1C2025;
      transition: all 120ms ease;

      > svg {
        font-size: 22px;
      }

      &:hover {
        background-color: #F3F6F9;
        border-color: #C7D0DD;
      }

      &:disabled {
        opacity: 0.3;
        &:hover {
          border: 1px solid #DAE2ED;
          background-color: transparent;
        }
      }
    }
    `
);
